import axios, { AxiosInstance, AxiosResponse, CancelTokenSource } from "axios";

// Custom error class for handling API errors
export const errorMessages = {
  NETWORK_ERROR:
    "No response received from the server. Please check your network.",
  REQUEST_FAILED: "Request failed due to an unexpected error.",
  BAD_REQUEST: "Bad Request. Please check your input.",
  UNAUTHORIZED: "Unauthorized access. Please log in.",
  FORBIDDEN: "You do not have permission to access this resource.",
  NOT_FOUND: "Resource not found. Please check the URL or endpoint.",
  SERVER_ERROR: "Internal Server Error. Please try again later.",
  TIMEOUT_ERROR: "Request timed out. Please try again later.",
  GENERIC_ERROR: "An unexpected error occurred. Please try again.",
  REQUEST_CANCELED: "The request was canceled by the user.",
};

export class ApiError extends Error {
  status?: number;
  data?: any;
  canceled?: boolean;

  constructor(
    message: string,
    status?: number,
    data?: any,
    canceled?: boolean
  ) {
    super(message);
    this.status = status;
    this.data = data;
    this.canceled = canceled;
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

interface RetryConfig {
  maxRetries: number; // Maximum number of retries
  retryDelay: number; // Delay between retries in milliseconds
}

class ApiWrapper {
  protected apiClient: AxiosInstance;
  private defaultTimeout: number;
  private retryConfig?: RetryConfig;
  private cancelSource?: CancelTokenSource;

  // Accept timeout and retryConfig (with maxRetries and retryDelay)
  constructor(
    baseURL: string,
    timeout: number = 100000,
    retryConfig?: RetryConfig,
    header: Record<string, string> = {}
  ) {
    this.defaultTimeout = timeout;
    this.retryConfig = retryConfig;

    this.apiClient = axios.create({
      baseURL,
      timeout: this.defaultTimeout,
      headers: {
        "Content-Type": "application/json",
        ...header,
      },
    });
  }

  // Handle GET request with a generic return type
  async get<T>(
    endpoint: string,
    params: Record<string, any> = {},
    timeout?: number
  ): Promise<T> {
    return this.requestWithRetry<T>("get", endpoint, params, {}, timeout);
  }

  // Handle POST request with a generic return type
  async post<T>(
    endpoint: string,
    data: Record<string, any>,
    timeout?: number
  ): Promise<T> {
    return this.requestWithRetry<T>("post", endpoint, {}, data, timeout);
  }

  // Handle PUT request with a generic return type
  async put<T>(
    endpoint: string,
    data: Record<string, any>,
    timeout?: number
  ): Promise<T> {
    return this.requestWithRetry<T>("put", endpoint, {}, data, timeout);
  }

  // Handle DELETE request with a generic return type
  async delete<T>(endpoint: string, timeout?: number): Promise<T> {
    return this.requestWithRetry<T>("delete", endpoint, {}, {}, timeout);
  }

  // Retry logic for any HTTP method
  private async requestWithRetry<T>(
    method: "get" | "post" | "put" | "delete",
    endpoint: string,
    params: Record<string, any>,
    data: Record<string, any>,
    timeout?: number
  ): Promise<T> {
    const config = timeout ? { timeout } : { timeout: this.defaultTimeout };
    let retries = 0;
    let lastError: any;

    // Create a new cancel token source for each request
    this.cancelSource = axios.CancelToken.source();

    while (retries <= (this.retryConfig?.maxRetries || 0)) {
      try {
        const response: AxiosResponse<T> = await this.apiClient[method](
          endpoint,
          {
            ...config,
            params,
            data,
            cancelToken: this.cancelSource.token, // Attach cancel token to request
          }
        );
        return response.data;
      } catch (error) {
        // If the error is due to cancellation, treat it as a canceled request
        if (axios.isCancel(error)) {
          throw new ApiError(errorMessages.REQUEST_CANCELED, 0, error, true);
        }

        lastError = error;
        if (retries >= (this.retryConfig?.maxRetries || 0)) {
          this.handleError(error); // Log the error
          throw new ApiError("Max retries reached");
        }
        retries++;
        const delay = this.retryConfig?.retryDelay || 1000; // Default retry delay of 1 second
        console.log(`Retrying request... attempt ${retries}`);
        await this.delay(delay); // Wait before retrying
      }
    }

    // If we exhausted retries, throw the last encountered error
    this.handleError(lastError);
    throw new ApiError(
      "Request failed after retries",
      lastError.response?.status,
      lastError.response?.data
    );
  }

  // Utility function to create delay between retries
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // Cancel the current request
  cancelRequest(): void {
    if (this.cancelSource) {
      this.cancelSource.cancel();
      this.cancelSource = undefined; // Reset the cancel source after cancellation
    }
  }

  // Common error handler
  private handleError(error: any): void {
    let errorMessage = errorMessages.GENERIC_ERROR; // Default to a generic error message
    let statusCode: number | undefined = undefined;
    let errorData: any = undefined;

    // Check if the error is an AxiosError
    if (axios.isAxiosError(error)) {
      if (error.response) {
        // The request was made, and the server responded with a status code
        statusCode = error.response.status;
        errorData = error.response.data;

        // Categorize HTTP status codes and assign corresponding messages
        switch (statusCode) {
          case 400:
            errorMessage = errorMessages.BAD_REQUEST;
            break;
          case 401:
            errorMessage = errorMessages.UNAUTHORIZED;
            break;
          case 403:
            errorMessage = errorMessages.FORBIDDEN;
            break;
          case 404:
            errorMessage = errorMessages.NOT_FOUND;
            break;
          case 500:
            errorMessage = errorMessages.SERVER_ERROR;
            break;
          case 408:
            errorMessage = errorMessages.TIMEOUT_ERROR;
            break;
          default:
            errorMessage = `Error ${statusCode}: ${errorMessages.GENERIC_ERROR}`;
            break;
        }
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage = errorMessages.NETWORK_ERROR;
      } else {
        // Something happened in setting up the request
        errorMessage = `${errorMessages.REQUEST_FAILED}: ${error.message}`;
      }
    } else {
      // Non-Axios error, handle unexpected issues
      errorMessage = errorMessages.GENERIC_ERROR;
    }

    // Create and throw a custom error
    const apiError = new ApiError(errorMessage, statusCode, errorData);
    console.error("API request failed:", apiError);
  }
}

export default ApiWrapper;
